import React, { FC, useEffect } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import FacetTermList from '@components/music/filter/FacetTermList'
import {
  aggregationToFacet,
  getParamCategory,
  getSubFacetCategory,
  hasSubFacets,
} from '@components/music/filter/utils/helpers'
import { Facet } from '@components/music/filter/utils/types'
import useHeightAnimation from '@hooks/useHeightAnimation'
import useUpdateQueryParams from '@hooks/useUpdateQueryParams'
import { usePageApi } from '@providers/PageApiProvider'
import { SearchPageApiResponse } from '@providers/types/api-responses'

interface Props {
  facet: Facet
}

const FacetFilter: FC<Props> = ({ facet }: Props) => {
  const { category, terms: filters } = facet
  const paramCategory = getParamCategory(facet)

  const { height, handleClick, contentRef, isOpen, updateHeight } =
    useHeightAnimation<HTMLUListElement>()
  const { t } = useTranslation()
  const { params } = useUpdateQueryParams()
  const { pageData } = usePageApi<SearchPageApiResponse>()

  useEffect(() => {
    updateHeight()
  }, [isOpen, filters])

  // If the collapsed items are equal to the non-collapsed items - no need to collapse
  const isCollapsingNecessary = (): boolean => {
    const facetValues = new Set((params[paramCategory] ?? []) as string[])

    // Check to see if the category matches the slug filter (e.g. /music/piano-vocal)
    if (pageData?.slug_filter && pageData.slug_filter.category === facet.category)
      facetValues.add(pageData.slug_filter.value)
    const facetCount = facetValues.size

    // Get the number of sub-facets associated with facet to determine total facet count
    let subFacetCount = 0
    for (const facetValue of facetValues) {
      if (hasSubFacets(category, facetValue)) {
        const subFacetCategory = getSubFacetCategory(category, facetValue)
        const subFacet =
          subFacetCategory && pageData?.aggregations[subFacetCategory]
            ? aggregationToFacet(subFacetCategory, pageData?.aggregations[subFacetCategory], true)
            : null
        subFacetCount += subFacet?.terms.length ?? 0
      }
    }

    return !(Array.isArray(filters) && filters.length === facetCount + subFacetCount)
  }

  if (!filters.length) return null

  return (
    <div className="h-auto mx-4 md:mx-5 pl-3 pr-1 pt-3 pb-2.5 text-base border-b border-gray dark:border-gray-border-dm">
      {/* OPENS AND CLOSES ACCORDION */}
      <button
        className="flex justify-between w-full leading-4 items-center mb-1"
        onClick={handleClick}>
        <p className="text-md font-medium text-black dark:text-gray">
          {t(`titles.filter.facets.${category}`)}
        </p>
        {isCollapsingNecessary() && (
          <i
            className={classNames(
              'transition ease-in duration-300 hover:opacity-50 nf-icon nf-chevron-down translate-y-2 text-xl leading-[0.5rem] dark:text-gray',
              { 'rotate-180': isOpen }
            )}
          />
        )}
      </button>
      <div
        className={classNames('overflow-hidden relative')}
        style={{
          height,
          transition: `height 300ms cubic-bezier(0.4, 0, 1, 1)`,
        }}>
        <FacetTermList
          facet={facet}
          isFilterOpen={isOpen}
          ref={contentRef}
          onCollapseChange={() => {
            setTimeout(updateHeight, 0)
          }}
        />
      </div>
    </div>
  )
}

export default FacetFilter
